import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth, formatApiErrorDetail } from "@/lib/AuthContext";

/**
 * AuthCallback is hit by the Emergent Google OAuth flow at
 * `${origin}/auth/callback#session_id=...`.
 * We synchronously read the session_id, POST it to the backend to mint our
 * own session cookie, then redirect into the app.
 */
export default function AuthCallback() {
  const navigate = useNavigate();
  const { exchangeGoogleSession } = useAuth();
  const hasProcessed = useRef(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const hash = window.location.hash || "";
    const match = hash.match(/session_id=([^&]+)/);
    const sessionId = match ? decodeURIComponent(match[1]) : null;
    if (!sessionId) {
      navigate("/auth", { replace: true });
      return;
    }
    (async () => {
      try {
        await exchangeGoogleSession(sessionId);
        // Strip the fragment before continuing.
        window.history.replaceState(null, "", "/projects");
        navigate("/projects", { replace: true });
      } catch (err) {
        setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
      }
    })();
  }, [exchangeGoogleSession, navigate]);

  return (
    <div
      className="min-h-screen grid place-items-center fc-app-bg p-6"
      data-testid="auth-callback"
    >
      <div className="fc-card p-8 max-w-sm text-center">
        {error ? (
          <>
            <div className="fc-heading text-[#1C1C1C]">Sign-in failed</div>
            <p className="text-sm text-[#57534E] mt-2">{error}</p>
            <button
              onClick={() => navigate("/auth", { replace: true })}
              className="mt-5 text-sm text-[#1C1C1C] underline"
              data-testid="auth-callback-retry"
            >
              Back to sign in
            </button>
          </>
        ) : (
          <>
            <div className="fc-heading text-[#1C1C1C]">Signing you in…</div>
            <p className="text-sm text-[#78716C] mt-2">One moment.</p>
          </>
        )}
      </div>
    </div>
  );
}
